import React, { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import PortalLayout from "../components/PortalLayout";
import AdminSubNav from "../components/AdminSubNav";
import { useAdminAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { withImageVersion } from "../lib/imageVersion";
import { toast } from "sonner";
import { Upload, Loader2, Check } from "lucide-react";

/**
 * Admin "Batch artwork" page.
 * Drop a side-by-side composite (sketch LEFT / designed RIGHT) for any
 * angel — the server splits it into both panels and bumps imageVersion.
 * One composite per angel; pick the angel from the dropdown.
 */
export default function AdminArtworkBatch() {
    const { admin } = useAdminAuth();
    const [angels, setAngels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedNumber, setSelectedNumber] = useState("");
    const [mode, setMode] = useState("composite"); // "composite" | "sketch" | "design"
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [lastUploaded, setLastUploaded] = useState(null); // updated angel
    const inputRef = useRef(null);

    useEffect(() => {
        const load = async () => {
            try {
                const { data } = await api.get("/angels");
                setAngels(data);
                if (data.length && !selectedNumber) setSelectedNumber(data[0].number);
            } finally {
                setLoading(false);
            }
        };
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (admin === null) return null;
    if (!admin) return <Navigate to="/login" replace />;

    const upload = async (file) => {
        if (!file) return;
        if (!selectedNumber) {
            toast.error("Choose an Angel first.");
            return;
        }
        if (!file.type.startsWith("image/")) {
            toast.error("Please choose an image file.");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const endpoint = mode === "sketch"
                ? `/admin/angels/${selectedNumber}/sketch`
                : mode === "design"
                    ? `/admin/angels/${selectedNumber}/image`
                    : `/admin/angels/${selectedNumber}/composite`;
            const { data } = await api.post(endpoint, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(
                mode === "sketch"
                    ? `Angel ${selectedNumber} — original sketch saved${data.designs?.length ? "" : " (now the visible card image, open for selection)"}.`
                    : mode === "design"
                        ? `Angel ${selectedNumber} — new design added to the gallery.`
                        : `Angel ${selectedNumber} — composite sliced, new design added.`
            );
            setLastUploaded(data);
            setAngels((prev) => prev.map((a) => (a.number === data.number ? data : a)));
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setUploading(false);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        upload(e.dataTransfer.files?.[0]);
    };

    const selectedAngel = angels.find((a) => a.number === selectedNumber);

    const previewMain = lastUploaded?.mainImageUrl
        ? withImageVersion(lastUploaded.mainImageUrl, lastUploaded.imageVersion)
        : null;
    const previewSketch = lastUploaded?.placeholderImageUrl
        ? withImageVersion(lastUploaded.placeholderImageUrl, lastUploaded.placeholderImageVersion)
        : null;

    return (
        <PortalLayout>
            <section
                className="max-w-5xl mx-auto px-6 md:px-12 lg:px-16 pt-16 pb-32"
                data-testid="admin-batch-page"
            >
                <div className="mb-10">
                    <span className="font-label text-[11px] text-[#cfa052]">Admin · curator</span>
                    <h1
                        className="mt-4 font-serif-display text-5xl md:text-6xl tracking-tighter text-[#e8e4db]"
                        data-testid="admin-batch-title"
                    >
                        Slice a <span className="italic">composite</span>
                    </h1>
                    <p className="mt-4 font-poetic text-[#9a958a] max-w-2xl">
                        Drop a single side-by-side composite (sketch on the <span className="text-[#cfa052]">left</span>, designed version on the <span className="text-[#cfa052]">right</span>). The first upload saves both panels; every upload after that keeps the original sketch and <span className="text-[#cfa052]">adds a brand-new design</span> to the angel&apos;s gallery — earlier designs stay browsable for members.
                    </p>
                </div>

                <AdminSubNav />

                {loading ? (
                    <div
                        className="text-center py-32 font-poetic text-[#9a958a]"
                        data-testid="admin-batch-loading"
                    >
                        loading angels…
                    </div>
                ) : (
                    <div className="grid lg:grid-cols-2 gap-10">
                        {/* Upload column */}
                        <div>
                            <label className="font-label text-[10px] tracking-[0.25em] uppercase text-[#cfa052]">
                                Angel
                            </label>
                            <select
                                value={selectedNumber}
                                onChange={(e) => setSelectedNumber(e.target.value)}
                                data-testid="admin-batch-select"
                                className="mt-2 w-full bg-[#151311] border border-white/10 focus:border-[#cfa052]/60 focus:outline-none text-[#e8e4db] py-3 px-3 rounded-sm font-poetic"
                            >
                                {angels.map((a) => (
                                    <option key={a.number} value={a.number}>
                                        № {a.number} — {a.statusText || "(unnamed)"}{a.designs?.length > 1 ? ` · ${a.designs.length} designs` : ""}
                                    </option>
                                ))}
                            </select>

                            {selectedAngel && (
                                <div className="mt-6" data-testid="admin-batch-gallery">
                                    <span className="font-label text-[10px] tracking-[0.25em] uppercase text-[#cfa052]">
                                        Current gallery — № {selectedAngel.number}
                                    </span>
                                    {selectedAngel.hasArtwork ? (
                                        <div className="mt-3 flex flex-wrap gap-3">
                                            {(selectedAngel.designs || []).map((d) => (
                                                <figure key={d.id} className="w-24" data-testid={`admin-batch-gallery-design-${d.id}`}>
                                                    <img
                                                        src={withImageVersion(d.url, d.version)}
                                                        alt={`Angel ${selectedAngel.number} ${d.label}`}
                                                        className="w-24 h-28 object-cover bg-[#0c0b0a] border border-white/10 rounded-sm"
                                                    />
                                                    <figcaption className="mt-1 font-label text-[8px] tracking-[0.15em] uppercase text-[#9a958a] leading-tight">
                                                        {d.label}
                                                        {d.uploadedAt && (
                                                            <span className="block text-[#6e6a62] normal-case tracking-normal font-poetic">
                                                                {new Date(d.uploadedAt).toLocaleDateString()}
                                                            </span>
                                                        )}
                                                    </figcaption>
                                                </figure>
                                            ))}
                                            {selectedAngel.placeholderImageUrl && (
                                                <figure className="w-24" data-testid="admin-batch-gallery-sketch">
                                                    <img
                                                        src={withImageVersion(selectedAngel.placeholderImageUrl, selectedAngel.placeholderImageVersion)}
                                                        alt={`Angel ${selectedAngel.number} sketch`}
                                                        className="w-24 h-28 object-cover bg-[#0c0b0a] border border-dashed border-white/15 rounded-sm opacity-80"
                                                    />
                                                    <figcaption className="mt-1 font-label text-[8px] tracking-[0.15em] uppercase text-[#6e6a62]">
                                                        Original sketch
                                                    </figcaption>
                                                </figure>
                                            )}
                                        </div>
                                    ) : (
                                        <p className="mt-3 font-poetic text-[#6e6a62] text-sm" data-testid="admin-batch-gallery-empty">
                                            No artwork yet — the next composite becomes <span className="text-[#cfa052]">Design I</span> and saves the original sketch.
                                        </p>
                                    )}
                                </div>
                            )}

                            <div className="mt-6">
                                <label className="font-label text-[10px] tracking-[0.25em] uppercase text-[#cfa052]">
                                    Upload type
                                </label>
                                <div className="mt-2 flex flex-wrap gap-2" role="radiogroup" aria-label="Upload type" data-testid="admin-batch-mode">
                                    {[
                                        { id: "composite", label: "Composite (sketch | design)" },
                                        { id: "sketch", label: "Original sketch only" },
                                        { id: "design", label: "Design only" },
                                    ].map((m) => (
                                        <button
                                            key={m.id}
                                            type="button"
                                            role="radio"
                                            aria-checked={mode === m.id}
                                            onClick={() => setMode(m.id)}
                                            data-testid={`admin-batch-mode-${m.id}`}
                                            className={`font-label text-[10px] tracking-[0.2em] uppercase px-4 py-2 rounded-full border transition-colors duration-300 ${
                                                mode === m.id
                                                    ? "bg-[#cfa052] border-[#cfa052] text-[#0c0b0a]"
                                                    : "border-white/10 bg-[#151311] text-[#9a958a] hover:text-[#e8e4db]"
                                            }`}
                                        >
                                            {m.label}
                                        </button>
                                    ))}
                                </div>
                                <p className="mt-2 font-poetic text-xs text-[#6e6a62]" data-testid="admin-batch-mode-hint">
                                    {mode === "sketch"
                                        ? "Saves the image as the angel's original sketch. If the angel has no designs yet, the sketch becomes its visible card image and the angel opens for selection."
                                        : mode === "design"
                                            ? "Adds a single designed image as the angel's next design — the sketch is untouched."
                                            : "A side-by-side image: sketch LEFT, designed RIGHT. First upload saves both panels; later uploads keep the sketch and add a new design."}
                                </p>
                            </div>

                            <div
                                className={`mt-6 border-2 border-dashed rounded-sm p-10 text-center cursor-pointer transition-colors ${
                                    dragOver
                                        ? "border-[#cfa052] bg-[#1a1612]"
                                        : "border-white/15 hover:border-[#cfa052]/50 bg-[#151311]/50"
                                }`}
                                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                onDragLeave={() => setDragOver(false)}
                                onDrop={onDrop}
                                onClick={() => inputRef.current?.click()}
                                data-testid="admin-batch-dropzone"
                            >
                                {uploading ? (
                                    <div className="flex flex-col items-center gap-3 text-[#cfa052]">
                                        <Loader2 className="w-8 h-8 animate-spin" />
                                        <span className="font-label text-[10px] tracking-[0.25em] uppercase">
                                            Slicing…
                                        </span>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center gap-3">
                                        <Upload className="w-8 h-8 text-[#9a958a]" />
                                        <p className="font-poetic text-[#e8e4db]">
                                            Drag a composite here, or <span className="text-[#cfa052] underline underline-offset-4">click to browse</span>
                                        </p>
                                        <p className="font-poetic text-xs text-[#6e6a62]">
                                            JPEG / PNG up to 20 MB. Sketch on the left, designed on the right.
                                        </p>
                                    </div>
                                )}
                                <input
                                    ref={inputRef}
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    data-testid="admin-batch-file-input"
                                    onChange={(e) => upload(e.target.files?.[0])}
                                />
                            </div>
                        </div>

                        {/* Preview column */}
                        <div>
                            <span className="font-label text-[10px] tracking-[0.25em] uppercase text-[#cfa052]">
                                Last sliced
                            </span>
                            {lastUploaded ? (
                                <div className="mt-3" data-testid="admin-batch-preview">
                                    <div className="flex items-center gap-2 mb-3">
                                        <Check className="w-4 h-4 text-[#cfa052]" />
                                        <span className="font-poetic text-[#e8e4db]">
                                            Angel {lastUploaded.number} updated · v{lastUploaded.imageVersion}
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <figure className="bg-[#0c0b0a] border border-white/10 rounded-sm overflow-hidden">
                                            <img
                                                src={previewSketch}
                                                alt={`Angel ${lastUploaded.number} sketch`}
                                                className="w-full h-auto object-contain"
                                            />
                                            <figcaption className="px-3 py-2 font-label text-[10px] tracking-[0.25em] uppercase text-[#9a958a]">
                                                Sketch (LEFT)
                                            </figcaption>
                                        </figure>
                                        <figure className="bg-[#0c0b0a] border border-white/10 rounded-sm overflow-hidden">
                                            <img
                                                src={previewMain}
                                                alt={`Angel ${lastUploaded.number} designed`}
                                                className="w-full h-auto object-contain"
                                            />
                                            <figcaption className="px-3 py-2 font-label text-[10px] tracking-[0.25em] uppercase text-[#9a958a]">
                                                Designed (RIGHT)
                                            </figcaption>
                                        </figure>
                                    </div>
                                </div>
                            ) : (
                                <p
                                    className="mt-3 font-poetic text-[#6e6a62] text-sm"
                                    data-testid="admin-batch-preview-empty"
                                >
                                    Upload a composite to see the split here.
                                </p>
                            )}
                        </div>
                    </div>
                )}
            </section>
        </PortalLayout>
    );
}
